<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <div class="p-relative header-image pb-5"></div>

    <b-container class="custom-container mt-5">
      <b-row>
        <b-col cols="12" class="px-3">
          <open-to-work-tabs />

          <template v-if="job">
            <div
              class="d-flex flex-wrap align-items-start justify-content-between pt-3 pb-2"
            >
              <div class="mb-2">
                <h4 class="font-weight-bold mb-1">
                  {{ job.title }}
                  <b-badge
                    v-if="job.has_withdrawn"
                    variant="secondary"
                    pill
                    class="ml-2 align-middle"
                  >
                    Withdrawn
                  </b-badge>
                </h4>
                <div class="text-muted">{{ job.company.name }}</div>
              </div>
              <b-button
                variant="outline-secondary"
                class="bg-white text-dark back-btn"
                :to="backTo"
              >
                {{ backLabel }}
              </b-button>
            </div>

            <div class="job-panel mb-4">
              <b-row class="job-facts">
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Employment type</div>
                  <div>{{ employmentTypeLabel(job.employment_type) }}</div>
                </b-col>
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Trade</div>
                  <div>{{ job.trade ? job.trade.name : '-' }}</div>
                </b-col>
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Location</div>
                  <div>{{ locationLabel(job) }}</div>
                </b-col>
                <template v-if="job.is_fifo">
                  <b-col md="4" class="mb-3">
                    <div class="fact-label">FIFO point</div>
                    <div>{{ job.fifo_point_location || '-' }}</div>
                  </b-col>
                  <b-col md="4" class="mb-3">
                    <div class="fact-label">Site</div>
                    <div>{{ job.site_location || '-' }}</div>
                  </b-col>
                </template>
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Pay</div>
                  <div>{{ job.pay ? payLabel(job) : 'Not shown' }}</div>
                </b-col>
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Posted</div>
                  <div>{{ formatDate(job.published_at) }}</div>
                </b-col>
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Closes</div>
                  <div>{{ formatDate(job.expires_at) }}</div>
                </b-col>
              </b-row>

              <job-listing-details :job="job" />

              <div class="fact-label">Description</div>
              <!-- Plain text interpolation (never v-html); pre-wrap keeps line breaks. -->
              <p class="job-description mb-0">{{ job.description }}</p>
            </div>

            <div class="job-panel mb-5">
              <!-- The company shortlisted them and asked to get in touch. -->
              <contact-share-request
                v-if="contactRequested"
                class="mt-0 mb-3"
                :company-name="job.company.name"
                :busy="isActing"
                @share="onShareContact"
              />
              <p v-else-if="contactShared" class="text-muted">
                You shared your contact details with {{ job.company.name }}.
              </p>
              <template v-if="job.has_applied">
                <div
                  class="d-flex flex-wrap align-items-center justify-content-between"
                >
                  <div>
                    <b-badge :variant="statusVariant" class="mr-2">
                      {{ statusLabel }}
                    </b-badge>
                    <span v-if="applicationStatus === 'new'" class="text-muted">
                      The company will see your application.
                    </span>
                  </div>
                  <b-button
                    v-if="canWithdraw"
                    variant="outline-secondary"
                    class="job-action-btn"
                    :disabled="isActing"
                    @click="showWithdrawModal = true"
                  >
                    Withdraw
                  </b-button>
                </div>
              </template>
              <template v-else>
                <p v-if="job.has_withdrawn" class="text-muted mb-3">
                  You withdrew your application. You can apply again below.
                </p>
                <div class="fact-label">Message to the company (optional)</div>
                <b-form-textarea
                  v-model="message"
                  rows="4"
                  :maxlength="maxMessageLength"
                  placeholder="Introduce yourself and say why you're a good fit"
                  class="mb-2"
                />
                <div class="text-right mb-3">
                  <small class="text-muted">
                    {{ message.length }}/{{ maxMessageLength }}
                  </small>
                </div>
                <div
                  class="d-flex flex-wrap align-items-center justify-content-between"
                >
                  <b-form-checkbox
                    v-if="hasResume"
                    v-model="includeResume"
                    class="mb-2"
                  >
                    Include my resume
                  </b-form-checkbox>
                  <small v-else class="text-muted mb-2">
                    Want to include a resume?
                    <b-link
                      :to="{ name: 'user.settings-page', hash: '#profile' }"
                    >
                      Add one to your profile
                    </b-link>
                  </small>
                  <b-button
                    variant="primary"
                    class="job-action-btn"
                    :disabled="isActing"
                    @click="onApply"
                  >
                    {{ job.has_withdrawn ? 'Reapply' : 'Apply' }}
                  </b-button>
                </div>
              </template>
            </div>
          </template>
        </b-col>
      </b-row>
    </b-container>

    <confirmation-modal
      :show-modal="showWithdrawModal"
      title="Withdraw Application"
      message="Are you sure you want to withdraw your application for this job?"
      ok-label="YES, WITHDRAW"
      @close="showWithdrawModal = false"
      @ok="onWithdraw"
    />
  </b-overlay>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue'
import ContactShareRequest from '@/components/jobPostings/ContactShareRequest.vue'
import JobListingDetails from '@/components/jobPostings/JobListingDetails.vue'
import OpenToWorkTabs from '@/components/openToWork/OpenToWorkTabs.vue'
import apiToasts from '@/mixins/apiToasts'
import {
  canWithdrawStatus,
  employmentTypeLabel,
  formatDate,
  locationLabel,
  myApplicationStatus,
  payLabel,
  tradieStatusText,
  tradieStatusVariant,
} from '@/utils/jobPostings'

export default {
  name: 'OpenToWorkJobPage',

  components: {
    ConfirmationModal,
    ContactShareRequest,
    JobListingDetails,
    OpenToWorkTabs,
  },

  mixins: [apiToasts],

  // The job page is reachable from the jobs list and from My applications, so
  // remember where we came from and send Back there (with its page/filters
  // intact). A direct load or refresh has no origin and falls back to the list.
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from && from.name && from.name !== 'open-to-work.job-page') {
        vm.origin = { name: from.name, fullPath: from.fullPath }
      }
    })
  },

  data() {
    return {
      origin: null,
      isLoading: false,
      isActing: false,
      message: '',
      // Ticked by default: sharing a resume is what makes an application useful.
      includeResume: true,
      // Mirrors JobApplication::MAX_MESSAGE_LENGTH in the API.
      maxMessageLength: 1000,
      showWithdrawModal: false,
    }
  },

  computed: {
    ...mapGetters('jobBoard', { job: 'currentJob' }),
    ...mapState({
      user: (state) => state.auth.user,
    }),

    hasResume() {
      return !!(this.user && this.user.resume)
    },

    applicationStatus() {
      return myApplicationStatus(this.job)
    },

    statusLabel() {
      return tradieStatusText(this.applicationStatus)
    },

    statusVariant() {
      return tradieStatusVariant(this.applicationStatus)
    },

    canWithdraw() {
      return canWithdrawStatus(this.applicationStatus)
    },

    contactRequested() {
      const app = this.job && this.job.my_application
      return !!app && app.contact_share_status === 'requested'
    },

    contactShared() {
      const app = this.job && this.job.my_application
      return !!app && app.contact_share_status === 'shared'
    },

    backTo() {
      return this.origin
        ? this.origin.fullPath
        : { name: 'open-to-work.jobs-page' }
    },

    backLabel() {
      const labels = {
        'open-to-work.applications-page': 'Back to Applications',
        'open-to-work.profile-page': 'Back to Profile',
        'open-to-work.requests-page': 'Back to Requests',
      }
      return (this.origin && labels[this.origin.name]) || 'Back to Jobs'
    },
  },

  async created() {
    this.isLoading = true
    try {
      await this.fetchJob(this.$route.params.jobId)
    } catch (err) {
      // A closed, expired or removed job is a 404 on the board.
      this.showToast('Error', 'This job is no longer available.', 'danger')
      this.$router.replace({ name: 'open-to-work.jobs-page' })
    } finally {
      this.isLoading = false
    }
  },

  methods: {
    ...mapActions('jobBoard', [
      'fetchJob',
      'applyToJob',
      'withdrawApplication',
      'shareContact',
    ]),

    employmentTypeLabel,
    formatDate,
    locationLabel,
    payLabel,

    async onApply() {
      this.isActing = true
      try {
        await this.applyToJob({
          id: this.job.id,
          message: this.message.trim(),
          includeResume: this.hasResume && this.includeResume,
        })
        this.message = ''
        this.showToast('Success', 'Application sent.')
      } catch (err) {
        this.onError(err, 'Could not send your application.')
      } finally {
        this.isActing = false
      }
    },

    async onShareContact() {
      this.isActing = true
      try {
        await this.shareContact(this.job.my_application.id)
        this.showToast('Success', 'Your contact details were shared.')
      } catch (err) {
        this.onError(err, 'Could not share your contact details.')
      } finally {
        this.isActing = false
      }
    },

    async onWithdraw() {
      this.showWithdrawModal = false
      this.isActing = true
      try {
        await this.withdrawApplication(this.job.id)
        this.showToast('Success', 'Application withdrawn.')
      } catch (err) {
        this.onError(err, 'Could not withdraw your application.')
      } finally {
        this.isActing = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Gray values follow the newer Tailwind-style scale in DESIGN.md.
.job-panel {
  background: #fff;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0px 10px 25px #00429d0d;
}

.fact-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.job-description {
  color: #111827;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

// Override the global 56px-tall .btn (same sizing as the business-admin job
// page): a fixed, centred height keeps the label from sitting high.
.back-btn,
.job-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  min-width: 110px;
  padding: 0 20px;
  font-weight: 600;
}
</style>
